import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, X, RefreshCw, BarChart } from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import { cashFlowForecastService } from '../../services/cashFlowForecastService';

const CreateForecast = () => {
  const navigate = useNavigate();
  const { id: editingForecastId } = useParams();
  const { success, error: showError, toasts, removeToast } = useToast();

  const [isProcessing, setIsProcessing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forecastFormData, setForecastFormData] = useState({
    forecastName: '',
    forecastType: 'Short-term',
    startDate: '',
    endDate: '',
    currency: 'USD',
    assumptions: '',
    generatedBy: 'Manual',
  });

  const getDateInputValue = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  useEffect(() => {
    const loadEditingData = async () => {
      if (editingForecastId) {
        setLoading(true);
        try {
          const response = await cashFlowForecastService.getForecast(editingForecastId);
          if (response.success && response.data) {
            const forecast = response.data;
            setForecastFormData({
              forecastName: forecast.forecastName || '',
              forecastType: forecast.forecastType || 'Short-term',
              startDate: getDateInputValue(forecast.startDate),
              endDate: getDateInputValue(forecast.endDate),
              currency: forecast.currency || 'USD',
              assumptions: Array.isArray(forecast.assumptions)
                ? forecast.assumptions.join(', ')
                : '',
              generatedBy: forecast.generatedBy || 'Manual',
            });
          }
        } catch (error) {
          showError(error.message || 'Failed to load forecast for editing');
        } finally {
          setLoading(false);
        }
      }
    };
    loadEditingData();
  }, [editingForecastId, showError]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForecastFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleForecastFormSubmit = async (event) => {
    event.preventDefault();
    setIsProcessing(true);
    try {
      const payload = {
        forecastName: forecastFormData.forecastName.trim(),
        forecastType: forecastFormData.forecastType,
        startDate: forecastFormData.startDate,
        endDate: forecastFormData.endDate,
        currency: forecastFormData.currency,
        assumptions: forecastFormData.assumptions
          ? forecastFormData.assumptions
              .split(',')
              .map((item) => item.trim())
              .filter(Boolean)
          : [],
        generatedBy: forecastFormData.generatedBy,
      };

      if (editingForecastId) {
        await cashFlowForecastService.updateForecast(editingForecastId, payload);
        success('Forecast updated successfully');
      } else {
        await cashFlowForecastService.createForecast(payload);
        success('Forecast created successfully');
      }
      
      // Navigate back to cashflow list
      navigate('/finance/cashflow');
    } catch (error) {
      showError(error.message || 'Failed to submit forecast');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    navigate('/finance/cashflow');
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    fontSize: '14px',
    fontFamily: 'var(--font-family)',
    backgroundColor: 'white',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px',
    fontFamily: 'var(--font-family)',
  };

  const headerActions = [
    {
      label: 'Cancel',
      icon: <X size={18} />,
      onClick: handleCancel,
      variant: 'outline',
      tooltip: 'Cancel and go back',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw size={24} className="spinning text-gray-400" />
      </div>
    );
  }

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title={editingForecastId ? "Edit Forecast" : "Create Forecast"}
        description="Plan and structure a new cash flow forecast for your operations"
        actions={headerActions}
      />

      <div className="page-container" style={{ width: '100%', maxWidth: '100%' }}>
        <div style={{ width: '100%', margin: '0 auto' }}>
          <form onSubmit={handleForecastFormSubmit}>
            <div
              style={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
                marginBottom: '24px',
                overflow: 'hidden',
              }}
            >
              {/* Card Header */}
              <div
                style={{
                  padding: '16px 24px',
                   borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f8f9fa',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                }}
              >
                <BarChart size={18} style={{ color: 'var(--primary-color)' }} />
                <h3
                  style={{
                    margin: 0,
                    fontSize: '15px',
                    fontWeight: '600',
                    color: '#111827',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  Forecast Details
                </h3>
              </div>

              {/* Card Body */}
              <div style={{ padding: '24px' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '20px',
                  }}
                >
                  {/* Forecast Name */}
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Forecast Name *</label>
                    <input
                      type="text"
                      name="forecastName"
                      value={forecastFormData.forecastName}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    />
                  </div>

                  {/* Forecast Type */}
                  <div>
                    <label style={labelStyle}>Forecast Type *</label>
                    <select
                      name="forecastType"
                      value={forecastFormData.forecastType}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      <option value="Short-term">Short-term</option>
                      <option value="Medium-term">Medium-term</option>
                      <option value="Long-term">Long-term</option>
                    </select>
                  </div>
                  
                  {/* Currency */}
                  <div>
                    <label style={labelStyle}>Currency *</label>
                    <input
                      type="text"
                      name="currency"
                      value={forecastFormData.currency}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    />
                  </div>

                  {/* Start Date */}
                  <div>
                    <label style={labelStyle}>Start Date *</label>
                    <input
                      type="date"
                      name="startDate"
                      value={forecastFormData.startDate}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    />
                  </div>

                  {/* End Date */}
                  <div>
                    <label style={labelStyle}>End Date *</label>
                    <input
                      type="date"
                      name="endDate"
                      value={forecastFormData.endDate}
                      onChange={handleChange}
                      required
                      style={inputStyle}
                    />
                  </div>

                  {/* Generated By */}
                  <div>
                    <label style={labelStyle}>Generated By *</label>
                    <select
                      name="generatedBy"
                      value={forecastFormData.generatedBy}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      <option value="Manual">Manual</option>
                      <option value="Rule-Based">Rule-Based</option>
                      <option value="AI">AI</option>
                    </select>
                  </div>

                  {/* Assumptions */}
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={labelStyle}>Assumptions (comma-separated)</label>
                    <textarea
                      name="assumptions"
                      rows={3}
                      value={forecastFormData.assumptions}
                      onChange={handleChange}
                      style={{
                        ...inputStyle,
                        resize: 'vertical',
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Form Actions - Now inside the card */}
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end',
                  padding: '20px 24px',
                  backgroundColor: '#f8f9fa',
                  borderTop: '1px solid #e5e7eb',
                }}
              >
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '500',
                    fontFamily: 'var(--font-family)',
                    color: '#374151',
                    backgroundColor: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isProcessing}
                  style={{
                    padding: '10px 24px',
                    fontSize: '14px',
                    fontWeight: '600',
                    fontFamily: 'var(--font-family)',
                    color: 'white',
                    backgroundColor: 'var(--primary-color)',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    opacity: isProcessing ? 0.7 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  {isProcessing ? (
                    <>
                      <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      {editingForecastId ? 'Save Changes' : 'Create Forecast'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default CreateForecast;
